import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Amount } from './api'
import {
  deleteSubscriptionFeeOverride,
  getAllRelations,
  getAllTemplates,
  getSubscriptionFee,
  getTemplateOnCustomer,
  setSubscriptionFee,
  setTemplateOnCustomer,
} from './endpoints'

export const QueryKeys = {
  CustomerTemplate: (customerId: string) => ['customer-template', customerId] as const,
  SetCustomerTemplate: (customerId: string) => ['set-customer-template', customerId] as const,
  CustomerBillablePrices: (customerId: string) => ['customer-billable-prices', customerId] as const,
  TemplateRelations: (templateId?: string) => ['template-relations', templateId] as const,
  AllTemplateRelations: (templateId?: string) => ['all-template-relations', templateId] as const,
  AllTemplates: () => ['all-templates'] as const,
  SubscriptionFee: (customerId: string) => ['subscription-fee', customerId] as const,
  DeleteSubscriptionFee: (customerId: string) => ['delete-subscription-fee', customerId] as const,
}

export const useCustomerTemplate = (customerId: string) => {
  return useQuery({
    queryKey: QueryKeys.CustomerTemplate(customerId),
    queryFn: () => getTemplateOnCustomer(customerId),
  })
}

export const useSetCustomerTemplate = (customerId: string) => {
  const client = useQueryClient()

  return useMutation({
    mutationKey: QueryKeys.SetCustomerTemplate(customerId),
    mutationFn: ({ templateId, keepCustomerOverrides }: { templateId: string; keepCustomerOverrides: boolean }) => {
      return setTemplateOnCustomer(customerId, templateId, keepCustomerOverrides)
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: QueryKeys.CustomerTemplate(customerId) })
      client.invalidateQueries({ queryKey: QueryKeys.SubscriptionFee(customerId) })
    },
  })
}

export const useAllTemplateRelations = () => {
  return useQuery({
    queryKey: QueryKeys.AllTemplateRelations(),
    queryFn: () => getAllRelations(),
  })
}

export const useTemplateRelations = (templateId: string | undefined) => {
  return useQuery({
    queryKey: QueryKeys.TemplateRelations(templateId),
    queryFn: () => getAllRelations(templateId),
    keepPreviousData: true,
    enabled: !!templateId,
  })
}

export const useAllTemplates = () => {
  return useQuery({
    queryKey: QueryKeys.AllTemplates(),
    queryFn: () => getAllTemplates(),
  })
}

export const useSubscriptionFee = (customerId: string) => {
  return useQuery({
    queryKey: QueryKeys.SubscriptionFee(customerId),
    queryFn: () => getSubscriptionFee(customerId),
  })
}

export const useSetSubscriptionFee = (customerId: string) => {
  const client = useQueryClient()

  return useMutation({
    mutationKey: QueryKeys.SubscriptionFee(customerId),
    mutationFn: (subscriptionFee: Amount) => setSubscriptionFee(customerId, subscriptionFee),
    onSuccess: (data) => {
      client.setQueryData(QueryKeys.SubscriptionFee(customerId), data)
    },
  })
}

export const useDeleteSubscriptionFee = (customerId: string) => {
  const client = useQueryClient()

  return useMutation({
    mutationKey: QueryKeys.DeleteSubscriptionFee(customerId),
    mutationFn: () => deleteSubscriptionFeeOverride(customerId),
    onSuccess: (data) => {
      client.setQueryData(QueryKeys.SubscriptionFee(customerId), data)
    },
  })
}
